import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
// ---Third-party imports
import React from 'react';
// ---Framework imports
import { useMemo, useCallback, useState } from 'uix/hooks';
// ---Local imports
import './styles.global.css';
import styles from './styles.css';
// ---Exports
export function useTreeCellRenderer(rows, opts = {}) {
    const unit = opts.unit ?? 10;
    const rowHeight = opts.rowHeight ?? 24;
    const toggleSize = opts.toggleSize ?? 20;
    const gutter = opts.gutter ?? 5 // pushes everything slightly right
    ;
    const labelGap = opts.labelGap ?? 2 // space between toggle and label
    ;
    const showCollapsedStem = opts.stemWhenCollapsed ?? false;
    const nameAttr = opts.nameAttr ?? 'id';
    const parentAttr = opts.parentAttr ?? 'parent_id';
    const colX = (level)=>gutter + level * unit // canonical x for each depth column
    ;
    // Internal expansion state: names of nodes whose children are visible
    const [expanded, setExpanded] = useState(()=>new Set());
    // Build base relationships from the full dataset
    const base = useMemo(()=>{
        const parentOf = new Map();
        const children = new Map();
        for (const r of rows){
            const name = r[nameAttr];
            const parent = r[parentAttr] ?? null;
            parentOf.set(name, parent);
            if (!children.has(parent)) children.set(parent, []);
            children.get(parent).push(name);
        }
        const isLastSibling = new Map();
        const hasChildren = new Map();
        for (const [p, kids] of children.entries()){
            kids.forEach((k, i)=>isLastSibling.set(k, i === kids.length - 1));
            if (p !== null) hasChildren.set(p, kids.length > 0);
        }
        for (const r of rows){
            const name = r[nameAttr];
            if (!hasChildren.has(name)) hasChildren.set(name, (children.get(name) ?? []).length > 0);
        }
        const depth = new Map();
        const memo = new Map();
        const getDepth = (n)=>{
            if (memo.has(n)) return memo.get(n);
            const p = parentOf.get(n);
            const d = p ? getDepth(p) + 1 : 0;
            memo.set(n, d);
            return d;
        };
        for (const r of rows)depth.set(r[nameAttr], getDepth(r[nameAttr]));
        return {
            parentOf,
            children,
            isLastSibling,
            hasChildren,
            depth
        };
    }, [
        rows,
        nameAttr,
        parentAttr
    ]);
    // A row is visible if all ancestors are expanded (roots always visible)
    const filteredRows = useMemo(()=>{
        return rows.filter((r)=>{
            let cur = base.parentOf.get(r[nameAttr]);
            while(cur != null){
                if (!expanded.has(cur)) return false;
                cur = base.parentOf.get(cur);
            }
            return true;
        });
    }, [
        rows,
        nameAttr,
        base.parentOf,
        expanded
    ]);
    // In the current (filtered) view, which nodes have at least one visible child?
    const hasVisibleChild = useMemo(()=>{
        const m = new Map();
        for (const r of filteredRows){
            const p = r[parentAttr];
            if (p != null) m.set(p, true);
        }
        // Ensure false for all visible nodes without visible children
        for (const r of filteredRows){
            const name = r[nameAttr];
            if (!m.has(name)) m.set(name, false);
        }
        return m;
    }, [
        filteredRows,
        nameAttr,
        parentAttr
    ]);
    // Ancestor mask for visible rows, using base sibling info
    const ancestorMask = useMemo(()=>{
        const m = new Map();
        for (const r of filteredRows){
            const name = r[nameAttr];
            const d = base.depth.get(name) || 0;
            const mask = new Array(Math.max(0, d - 1)).fill(false);
            let cur = name;
            for(let level = d - 2; level >= 0; level--){
                const parent = base.parentOf.get(cur);
                if (parent == null) break;
                mask[level] = !(base.isLastSibling.get(parent) ?? true);
                cur = parent;
            }
            m.set(name, mask);
        }
        return m;
    }, [
        filteredRows,
        nameAttr,
        base.depth,
        base.parentOf,
        base.isLastSibling
    ]);
    // Collect all descendants (names) of a node
    const getDescendants = useCallback((root)=>{
        const out = [];
        const stack = [
            ...base.children.get(root) ?? []
        ];
        while(stack.length){
            const n = stack.pop();
            out.push(n);
            const kids = base.children.get(n) ?? [];
            for (const k of kids)stack.push(k);
        }
        return out;
    }, [
        base
    ]);
    // Toggle a node; Ctrl/Meta-click toggles the entire subtree
    const handleToggle = useCallback((name, evt)=>{
        const withSubtree = !!(evt?.ctrlKey || evt?.metaKey);
        setExpanded((prev)=>{
            const next = new Set(prev);
            const isOpen = next.has(name);
            const targets = withSubtree ? [
                name,
                ...getDescendants(name)
            ] : [
                name
            ];
            if (isOpen) {
                targets.forEach((t)=>next.delete(t));
            } else {
                targets.forEach((t)=>next.add(t));
            }
            return next;
        });
    }, [
        getDescendants
    ]);
    const renderTreeCell = useCallback(({ original })=>{
        const row = original;
        const name = row[nameAttr];
        const depth = base.depth.get(name) ?? 0;
        const mask = ancestorMask.get(name) ?? [];
        const isLast = base.isLastSibling.get(name) ?? true;
        const hasKids = base.hasChildren.get(name) ?? false;
        const isOpen = expanded.has(name);
        const visibleChild = hasVisibleChild.get(name) ?? false;
        const svgHeight = rowHeight;
        const svgWidth = colX(depth) + unit // ensure room up to current column
        ;
        const parentX = depth > 0 ? colX(depth - 1) : null;
        const hereX = colX(depth);
        const toggleLeft = hereX - toggleSize / 3 // button center-aligned with lines
        ;
        const labelLeft = hereX + toggleSize + labelGap;
        const drawStem = hasKids && (visibleChild || isOpen || showCollapsedStem);
        const stemBottom = visibleChild || isOpen ? svgHeight : Math.min(svgHeight, svgHeight / 2 + 6);
        const glyphStyle = {
            left: toggleLeft,
            width: toggleSize,
            height: toggleSize,
            fontSize: Math.max(20, toggleSize * 1.2) + 'px'
        };
        return /*#__PURE__*/ _jsx(_Fragment, {
            children: /*#__PURE__*/ _jsxs("div", {
                className: styles.treeCell,
                style: {
                    height: rowHeight
                },
                children: [
                    /*#__PURE__*/ _jsxs("svg", {
                        width: svgWidth,
                        height: svgHeight,
                        className: styles.treeCellGuides,
                        "aria-hidden": "true",
                        children: [
                            mask.map((show, level)=>{
                                if (!show) return null;
                                const x = colX(level);
                                return /*#__PURE__*/ _jsx("line", {
                                    x1: x,
                                    y1: 0,
                                    x2: x,
                                    y2: svgHeight,
                                    stroke: "currentColor",
                                    strokeWidth: "1",
                                    vectorEffect: "non-scaling-stroke",
                                    opacity: "0.35"
                                }, `v-${level}`);
                            }),
                            depth > 0 && /*#__PURE__*/ _jsxs(_Fragment, {
                                children: [
                                    /*#__PURE__*/ _jsx("line", {
                                        x1: parentX,
                                        y1: svgHeight / 2,
                                        x2: hereX,
                                        y2: svgHeight / 2,
                                        stroke: "currentColor",
                                        strokeWidth: "1",
                                        vectorEffect: "non-scaling-stroke",
                                        opacity: "0.65"
                                    }),
                                    /*#__PURE__*/ _jsx("line", {
                                        x1: parentX,
                                        y1: 0,
                                        x2: parentX,
                                        y2: svgHeight / 2,
                                        stroke: "currentColor",
                                        strokeWidth: "1",
                                        vectorEffect: "non-scaling-stroke",
                                        opacity: "0.65"
                                    }),
                                    !isLast && /*#__PURE__*/ _jsx("line", {
                                        x1: parentX,
                                        y1: svgHeight / 2,
                                        x2: parentX,
                                        y2: svgHeight,
                                        stroke: "currentColor",
                                        strokeWidth: "1",
                                        vectorEffect: "non-scaling-stroke",
                                        opacity: "0.65"
                                    })
                                ]
                            }),
                            drawStem && /*#__PURE__*/ _jsx("line", {
                                x1: hereX,
                                y1: svgHeight / 2,
                                x2: hereX,
                                y2: stemBottom,
                                stroke: "currentColor",
                                strokeWidth: "1",
                                vectorEffect: "non-scaling-stroke",
                                opacity: "0.35"
                            })
                        ]
                    }),
                    hasKids ? /*#__PURE__*/ _jsx("button", {
                        className: styles.treeCellToggle,
                        onClick: (e)=>handleToggle(name, e),
                        title: "Click to toggle, Ctrl-Click to toggle subtree",
                        style: glyphStyle,
                        children: isOpen ? '▾' : '▸'
                    }) : /*#__PURE__*/ _jsx("span", {
                        className: styles.treeCellLeaf,
                        style: glyphStyle,
                        children: '🞄'
                    }),
                    /*#__PURE__*/ _jsx("div", {
                        className: styles.treeCellLabel,
                        style: {
                            paddingLeft: labelLeft
                        },
                        children: name
                    })
                ]
            })
        });
    }, [
        base.depth,
        base.isLastSibling,
        base.hasChildren,
        ancestorMask,
        hasVisibleChild,
        expanded,
        rowHeight,
        handleToggle,
        toggleSize,
        gutter,
        labelGap,
        unit,
        nameAttr
    ]);
    return {
        treeTableColumn: {
            Header: opts.Header || 'Name',
            id: '_treecell',
            accessor: null,
            Cell: renderTreeCell,
            style: {
                position: 'relative',
                padding: 0
            }
        },
        rows: filteredRows
    };
}
